/**
 * StatusBar - Bottom Status Bar Component
 *
 * Shows current workspace, tab count, user role, and quick toggles.
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress } from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  Cached as CacheIcon,
  Code as ScriptsIcon,
  Storage as EntitiesIcon,
  ClearAll as ClearAllIcon,
  Refresh as ReseedIcon,
  DataObject as FastIcon,
  AutoFixHigh as ReseedCacheIcon,
  Warehouse as WarehouseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import AtomIcon from '../common/AtomIcon';
import { ThemeToggleButton } from '../ThemeSelector';
import FlexService from '../../services/FlexService';
import AuthService from '../../services/AuthService';

/**
 * StatusBar Component
 */
const StatusBar = ({
  colors,
  height,
  activeWorkspace,
  tabCount,
  userRoles,
  aiDockOpen,
  onAiDockToggle,
}) => {
  // Get primary role for display
  const primaryRole = userRoles?.[0] || 'User';

  // Cache menu state
  const [cacheMenuAnchor, setCacheMenuAnchor] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);

  // Reseed menu state
  const [reseedMenuAnchor, setReseedMenuAnchor] = useState(null);
  const [reseedLoading, setReseedLoading] = useState(false);

  // Warehouse switcher state -- only shown when the user has access to more
  // than one warehouse. Backs onto UserController's pre-existing
  // GET/PUT /api/v1/users/me/warehouses + /me/active-warehouse, never wired
  // to any frontend UI before now.
  const [warehouses, setWarehouses] = useState([]);
  const [activeWarehouse, setActiveWarehouse] = useState('');
  const [warehouseMenuAnchor, setWarehouseMenuAnchor] = useState(null);
  const [warehouseSwitching, setWarehouseSwitching] = useState(false);
  const [warehouseError, setWarehouseError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    AuthService.getMyWarehouses()
      .then((data) => {
        if (cancelled) return;
        setWarehouses(data?.warehouses || []);
        setActiveWarehouse(data?.activeWarehouse || '');
      })
      .catch(() => {
        // Best-effort -- no warehouse switcher is a harmless degrade, not
        // worth surfacing an error for on every page load.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleWarehouseMenuOpen = (event) => {
    setWarehouseMenuAnchor(event.currentTarget);
  };

  const handleWarehouseMenuClose = () => {
    setWarehouseMenuAnchor(null);
  };

  const handleWarehouseSelect = async (warehouse) => {
    handleWarehouseMenuClose();
    if (warehouse.warehouseCode === activeWarehouse) {
      return;
    }
    setWarehouseSwitching(true);
    setWarehouseError(null);
    try {
      await AuthService.switchActiveWarehouse(warehouse.id);
      // Full reload -- simplest, safest way to make sure every already-loaded,
      // warehouse-scoped grid/dashboard re-fetches under the new scope.
      window.location.reload();
    } catch (error) {
      setWarehouseError(error.message || 'Failed to switch warehouse');
      setWarehouseSwitching(false);
      setTimeout(() => setWarehouseError(null), 5000);
    }
  };

  const handleCacheMenuOpen = (event) => {
    setCacheMenuAnchor(event.currentTarget);
  };

  const handleCacheMenuClose = () => {
    setCacheMenuAnchor(null);
  };

  const handleReseedMenuOpen = (event) => {
    setReseedMenuAnchor(event.currentTarget);
  };

  const handleReseedMenuClose = () => {
    setReseedMenuAnchor(null);
  };

  const handleReseedAction = async (action) => {
    setReseedLoading(true);
    setCacheStatus(null);
    handleReseedMenuClose();
    try {
      const result = await FlexService.executeScript({
        scriptName: 'system_reseed_data',
      });
      const ctx = result?.context || {};
      const reseedResult = ctx.result || {};
      const msg = reseedResult.message || result?.message || 'Reseed complete';

      if (action === 'reseed_and_cache') {
        // Also clear all caches after reseed
        await FlexService.evictAllCaches();
        setCacheStatus({ success: true, message: msg + ' + caches cleared' });
      } else {
        setCacheStatus({ success: true, message: msg });
      }
      setTimeout(() => setCacheStatus(null), 5000);
    } catch (error) {
      setCacheStatus({ success: false, message: error.message || 'Reseed failed' });
      setTimeout(() => setCacheStatus(null), 5000);
    } finally {
      setReseedLoading(false);
    }
  };

  const handleCacheAction = async (action) => {
    setCacheLoading(true);
    setCacheStatus(null);
    try {
      let result;
      switch (action) {
        case 'all':
          result = await FlexService.evictAllCaches();
          break;
        case 'scripts':
          result = await FlexService.evictScriptCaches();
          break;
        case 'entities':
          result = await FlexService.evictEntityCaches();
          break;
        default:
          return;
      }
      setCacheStatus({ success: true, message: result.message || 'Cache cleared' });
      // Auto-clear status after 3 seconds
      setTimeout(() => setCacheStatus(null), 3000);
    } catch (error) {
      setCacheStatus({ success: false, message: error.message || 'Failed to clear cache' });
      setTimeout(() => setCacheStatus(null), 5000);
    } finally {
      setCacheLoading(false);
      handleCacheMenuClose();
    }
  };

  return (
    <Box
      sx={{
        height,
        minHeight: height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        bgcolor: colors.surface,
        borderTop: `1px solid ${colors.border}`,
      }}
    >
      {/* Left Section - Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ReadyIcon sx={{ fontSize: 12, color: colors.success }} />
          <Typography
            variant="caption"
            sx={{ color: colors.textMuted, fontSize: '0.7rem' }}
          >
            Ready
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}
        >
          {activeWorkspace?.label || 'FAST Studio'}
        </Typography>

        {activeWorkspace?.supportsTabs && tabCount > 0 && (
          <Typography
            variant="caption"
            sx={{ color: colors.textMuted, fontSize: '0.7rem' }}
          >
            {tabCount} tab{tabCount !== 1 ? 's' : ''} open
          </Typography>
        )}
      </Box>

      {/* Right Section - Role & Toggles */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Cache status indicator */}
        {cacheStatus && (
          <Typography
            variant="caption"
            sx={{
              color: cacheStatus.success ? colors.success : colors.error,
              fontSize: '0.65rem',
            }}
          >
            {cacheStatus.message}
          </Typography>
        )}

        {/* Reseed data.sql button (menu) */}
        <Tooltip title="Reseed Database">
          <IconButton
            size="small"
            onClick={handleReseedMenuOpen}
            disabled={reseedLoading}
            sx={{
              p: 0.25,
              color: colors.textMuted,
              '&:hover': { color: colors.info },
            }}
          >
            {reseedLoading ? (
              <CircularProgress size={14} sx={{ color: colors.info }} />
            ) : (
              <ReseedIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Reseed menu */}
        <Menu
          anchorEl={reseedMenuAnchor}
          open={Boolean(reseedMenuAnchor)}
          onClose={handleReseedMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{
            sx: {
              bgcolor: colors.surface,
              border: `1px solid ${colors.border}`,
              minWidth: 220,
            },
          }}
        >
          <MenuItem
            onClick={() => handleReseedAction('reseed')}
            sx={{ fontSize: '0.8rem', py: 0.75 }}
          >
            <ListItemIcon>
              <FastIcon sx={{ fontSize: 16, color: colors.info }} />
            </ListItemIcon>
            <ListItemText
              primary="Reseed FAST data.sql"
              secondary="Re-execute data.sql on FAST DB"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
            />
          </MenuItem>
          <Divider sx={{ borderColor: colors.border }} />
          <MenuItem
            onClick={() => handleReseedAction('reseed_and_cache')}
            sx={{ fontSize: '0.8rem', py: 0.75 }}
          >
            <ListItemIcon>
              <ReseedCacheIcon sx={{ fontSize: 16, color: colors.warning }} />
            </ListItemIcon>
            <ListItemText
              primary="Reseed + Clear Cache"
              secondary="Reseed then clear all caches"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
            />
          </MenuItem>
        </Menu>

        {/* Cache reset button */}
        <Tooltip title="Reset Cache">
          <IconButton
            size="small"
            onClick={handleCacheMenuOpen}
            disabled={cacheLoading}
            sx={{
              p: 0.25,
              color: colors.textMuted,
              '&:hover': { color: colors.warning },
            }}
          >
            {cacheLoading ? (
              <CircularProgress size={14} sx={{ color: colors.warning }} />
            ) : (
              <CacheIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Cache menu */}
        <Menu
          anchorEl={cacheMenuAnchor}
          open={Boolean(cacheMenuAnchor)}
          onClose={handleCacheMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{
            sx: {
              bgcolor: colors.surface,
              border: `1px solid ${colors.border}`,
              minWidth: 200,
            },
          }}
        >
          <MenuItem
            onClick={() => handleCacheAction('all')}
            sx={{ fontSize: '0.8rem', py: 0.75 }}
          >
            <ListItemIcon>
              <ClearAllIcon sx={{ fontSize: 16, color: colors.warning }} />
            </ListItemIcon>
            <ListItemText
              primary="Clear All Caches"
              secondary="Scripts, entities, APIs"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
            />
          </MenuItem>
          <Divider sx={{ borderColor: colors.border }} />
          <MenuItem
            onClick={() => handleCacheAction('scripts')}
            sx={{ fontSize: '0.8rem', py: 0.75 }}
          >
            <ListItemIcon>
              <ScriptsIcon sx={{ fontSize: 16, color: colors.accent }} />
            </ListItemIcon>
            <ListItemText
              primary="Clear Script Cache"
              secondary="Resolution + parsed scripts"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => handleCacheAction('entities')}
            sx={{ fontSize: '0.8rem', py: 0.75 }}
          >
            <ListItemIcon>
              <EntitiesIcon sx={{ fontSize: 16, color: colors.info }} />
            </ListItemIcon>
            <ListItemText
              primary="Clear Entity Cache"
              secondary="Entity functions + APIs"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
              secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
            />
          </MenuItem>
        </Menu>

        <ThemeToggleButton size="small" />

        {/* Warehouse switcher -- only shown when the user has more than one
            warehouse; a single-warehouse user sees nothing extra here. */}
        {warehouseError && (
          <Typography
            variant="caption"
            sx={{ color: colors.error, fontSize: '0.65rem' }}
          >
            {warehouseError}
          </Typography>
        )}
        {warehouses.length > 1 && (
          <>
            <Tooltip title="Switch Warehouse">
              <Chip
                icon={
                  warehouseSwitching ? (
                    <CircularProgress size={12} sx={{ color: colors.textMuted }} />
                  ) : (
                    <WarehouseIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={activeWarehouse || 'Select Warehouse'}
                size="small"
                onClick={handleWarehouseMenuOpen}
                disabled={warehouseSwitching}
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: colors.surfaceElevated,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            </Tooltip>
            <Menu
              anchorEl={warehouseMenuAnchor}
              open={Boolean(warehouseMenuAnchor)}
              onClose={handleWarehouseMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  minWidth: 220,
                },
              }}
            >
              {warehouses.map((wh) => (
                <MenuItem
                  key={wh.id}
                  onClick={() => handleWarehouseSelect(wh)}
                  sx={{ fontSize: '0.8rem', py: 0.75 }}
                >
                  <ListItemIcon>
                    {wh.warehouseCode === activeWarehouse ? (
                      <CheckIcon sx={{ fontSize: 16, color: colors.success }} />
                    ) : (
                      <WarehouseIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={wh.warehouseName}
                    secondary={wh.warehouseCode}
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                    secondaryTypographyProps={{ fontSize: '0.65rem', color: colors.textMuted }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Chip
          label={primaryRole}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: colors.surfaceElevated,
            color: colors.textSecondary,
            '& .MuiChip-label': { px: 1 },
          }}
        />

        <Tooltip title={aiDockOpen ? 'Hide Atom (AI Assistant)' : 'Show Atom (AI Assistant)'}>
          <IconButton
            size="small"
            onClick={onAiDockToggle}
            sx={{
              p: 0.25,
              color: aiDockOpen ? colors.accent : colors.textMuted,
              '&:hover': { color: colors.accent },
            }}
          >
            <AtomIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default StatusBar;
